import { Box } from "./Box";
import { Button, IconButton } from "./Button";
import { FullScreenLayout } from "./FullScreenLayout";
import { PdfPreview } from "./PdfPreview";
import { getExtension, isExtensionImage, isExtensionJson, isExtensionPdf } from "../utils/files";
import { EmptyState } from "./EmptyState";
import unsupportedFilePreview from "../assets/img/unsupported-file-preview.svg";
import { downloadFile } from "../api";
import { Dialog } from "./Dialog";

import { useTranslation } from "../stores/RootStoreContext";
import { LoadingIndicator } from "./LoadingIndicator";
import { ReactNode, useEffect, useState } from "react";
import { logError } from "../utils/log";
import { isTMessage, TFunction, TMessage } from "../i18n/tt";

type FilePreviewDialogProps = FilePreviewProps & {
  onClose?: () => void;
};

export type FilePreviewProps = {
  filename?: string | TMessage;
  file: { url: string } | { data: ArrayBuffer | Blob | File };
  width?: "sm" | "md" | "lg" | number;
  extraContent?: ReactNode;
};

export function FilePreview({ filename = "", file, width, extraContent }: FilePreviewProps) {
  const { t } = useTranslation();
  const actualFilename = toFilename(filename, t) ?? "";
  const handleDownload = () => downloadFile(file, actualFilename);
  return (
    <Box justifyContent={"center"} marginX={"auto"} marginTop marginBottom>
      {isExtensionPdf(actualFilename) ? (
        <PdfPreview file={file} width={width} extraContent={extraContent} />
      ) : isExtensionImage(actualFilename) && "url" in file ? (
        <ImagePreview filename={actualFilename} url={file.url} />
      ) : isExtensionJson(actualFilename) ? (
        <JsonPreview file={file} />
      ) : (
        <UnsupportedFile filename={actualFilename} onDownload={handleDownload} />
      )}
    </Box>
  );
}

function toFilename(filename: string | TMessage | undefined, t: TFunction): string | undefined {
  if (filename === undefined) {
    return undefined;
  } else if (isTMessage(filename)) {
    return t(filename);
  } else {
    return filename;
  }
}

export function FilePreviewDialog(props: FilePreviewDialogProps) {
  const { t } = useTranslation();
  const filename = toFilename(props.filename, t);
  return (
    <Dialog fullscreen onCancel={props.onClose}>
      <FullScreenLayout
        pageHeader={
          <Box bg borderBottom padding alignItems={"center"} flex={"none"}>
            {props.onClose && (
              <IconButton
                icon={"x"}
                tooltip={{ key: "close" }}
                onClick={props.onClose}
                color={"inherit"}
              />
            )}
            {filename && (
              <>
                <div>{filename}</div>
                <Button
                  className={"ml-auto"}
                  icon={"download"}
                  label={{ key: "download" }}
                  variant={"secondary"}
                  onClick={() => downloadFile(props.file, filename)}
                />
              </>
            )}
          </Box>
        }
        centerPanel={<FilePreview {...props} />}
      />
    </Dialog>
  );
}

function ImagePreview({ filename, url }: { filename: string; url: string }) {
  return <img src={url} alt={filename} className={"tw-max-w-full"} />;
}

function UnsupportedFile({ filename, onDownload }: { filename: string; onDownload?: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={<img src={unsupportedFilePreview} alt={"Unsupported file type illustration"} />}
      description={t("file-preview.unsupported-file-type", { extension: getExtension(filename) })}
      actions={
        onDownload ? (
          <Button
            variant={"secondary"}
            label={t("download")}
            icon={"download"}
            onClick={onDownload}
          />
        ) : undefined
      }
    />
  );
}

export type JsonPreviewProps = {
  file: { url: string } | { data: ArrayBuffer | Blob };
};

function JsonPreview({ file }: JsonPreviewProps) {
  const [data, setData] = useState<unknown | undefined>(undefined);

  useEffect(() => {
    if ("data" in file) {
      if (file.data instanceof ArrayBuffer) {
        setData(new TextDecoder("utf-8").decode(file.data));
      } else if (file.data instanceof Blob) {
        file.data.text().then(setData);
      } else {
        logError(":Not supported:", { file });
      }
    }
  }, [file]);

  useEffect(() => {
    if ("url" in file) {
      fetch(file.url)
        .then((response) => response.text())
        .then(setData);
    }
  }, [file]);
  if (data === undefined) {
    return <LoadingIndicator />;
  } else {
    return (
      <div>
        <pre
          style={{
            background: "#fff",
            padding: "0.5rem",
            boxShadow: "0 0 0 1px #e5e7eb",
            borderRadius: "0.375rem",
          }}
          dangerouslySetInnerHTML={{ __html: syntaxHighlight(data as string) }}
        />
      </div>
    );
  }
}

const colors = {
  string: "#a93d3d",
  number: "#b5cea8",
  boolean: "#569cd6",
  null: "#569cd6",
  key: "#1e517b",
} as const;

function syntaxHighlight(input: string) {
  let json;
  try {
    json = JSON.parse(input);
  } catch (e) {
    logError(e, "Failed to parse JSON", { input });
  }
  if (typeof json !== "string") {
    json = JSON.stringify(json, undefined, 2);
  }
  json = json.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+s-]?\d+)?)/g,
    function (match) {
      let type: keyof typeof colors = "number";
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          type = "key";
        } else {
          type = "string";
        }
      } else if (/true|false/.test(match)) {
        type = "boolean";
      } else if (/null/.test(match)) {
        type = "null";
      }
      return `<span style="color:${colors[type]}">${match}</span>`;
    },
  );
}
